// Invitation Service
// Frontend service for stakeholder invitation management.
// API calls go through the shared apiClient (bearer + 401 session-recovery).
import { apiFetch } from './apiClient.js';

/**
 * Create a new invitation
 */
export async function createInvitation({
  invitee_email,
  invitee_name,
  relationship_type = 'client',
  venn_link_id,
  message
}) {
  return apiFetch('/v1/invitations', {
    method: 'POST',
    body: { invitee_email, invitee_name, relationship_type, venn_link_id, message }
  });
}

/**
 * Create multiple invitations at once
 * @param {Array} invitations - Array of invitation objects
 * @param {Object} options - Options for bulk creation
 * @param {boolean} options.send_emails - Whether to send invitation emails (default: true)
 */
export async function createBulkInvitations(invitations, options = {}) {
  const { send_emails = true } = options;
  return apiFetch('/v1/invitations/bulk', {
    method: 'POST',
    body: { invitations, send_emails }
  });
}

// ============================================================================
// HubSpot Import Functions
// ============================================================================

/**
 * Get HubSpot contacts count
 */
export async function getHubspotContactsCount() {
  return apiFetch('/v1/hubspot/contacts/count');
}

/**
 * List HubSpot contacts with pagination
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of contacts to fetch
 * @param {string} options.after - Cursor for pagination
 * @param {string} options.search - Search query
 */
export async function listHubspotContacts({ limit = 50, after, search } = {}) {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());
  if (after) params.append('after', after);
  if (search) params.append('search', search);

  return apiFetch(`/v1/hubspot/contacts?${params}`);
}

/**
 * Start bulk import of all HubSpot contacts
 * @param {Object} options - Import options
 * @param {string} options.relationship_type - Default relationship type for imported contacts
 * @param {boolean} options.send_emails - Whether to send invitation emails
 */
export async function startHubspotBulkImport({ relationship_type = 'prospect', send_emails = false } = {}) {
  return apiFetch('/v1/hubspot/import-all', {
    method: 'POST',
    body: { relationship_type, send_emails }
  });
}

/**
 * Get import job status
 * @param {string} jobId - The import job ID
 */
export async function getImportJobStatus(jobId) {
  return apiFetch(`/v1/hubspot/import-jobs/${jobId}`);
}

/**
 * List sent invitations
 */
export async function listInvitations({ status, relationship_type, limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (relationship_type) params.append('relationship_type', relationship_type);
  if (limit) params.append('limit', limit.toString());
  if (offset) params.append('offset', offset.toString());

  return apiFetch(`/v1/invitations?${params}`);
}

/**
 * Get a specific invitation
 */
export async function getInvitation(id) {
  return apiFetch(`/v1/invitations/${id}`);
}

/**
 * Update an invitation (e.g., change relationship_type)
 * @param {string} id - The invitation ID
 * @param {Object} updates - Fields to update
 * @param {string} updates.relationship_type - New relationship type
 */
export async function updateInvitation(id, updates) {
  return apiFetch(`/v1/invitations/${id}`, { method: 'PATCH', body: updates });
}

/**
 * Resend an invitation email (or send first email for imported contacts)
 */
export async function resendInvitation(id) {
  return apiFetch(`/v1/invitations/${id}/resend`, { method: 'POST' });
}

/**
 * Accept an invitation as the authenticated invitee.
 *
 * Requires a Supabase session. The backend verifies the session's email
 * matches the invitation's `invitee_email` (case-insensitive).
 *
 * On 403 mismatch, the rejected response body carries `detail` plus
 * `invitee_email` and `signed_in_email` so the caller can render specific
 * guidance — the function rethrows an Error with those fields attached.
 */
export async function acceptInvitation(token) {
  try {
    return await apiFetch(`/v1/invitations/accept/${token}`, { method: 'POST' });
  } catch (err) {
    // apiFetch attaches `.status` and the parsed `.body`. Surface the
    // mismatch fields (403) the caller renders specific guidance from.
    const body = err.body || {};
    err.detail = body.detail || null;
    err.inviteeEmail = body.invitee_email || null;
    err.signedInEmail = body.signed_in_email || null;
    throw err;
  }
}

/**
 * Decline an invitation. Currently the public-token route — no auth
 * required. Kept symmetric with acceptInvitation for the frontend.
 */
export async function declineInvitation(token) {
  return (await apiFetch(`/v1/invitations/decline/${token}`, {
    method: 'POST',
    auth: false
  })) || {};
}

/**
 * Cancel a pending invitation
 */
export async function cancelInvitation(id) {
  return apiFetch(`/v1/invitations/${id}`, { method: 'DELETE' });
}

export const invitationService = {
  createInvitation,
  createBulkInvitations,
  listInvitations,
  getInvitation,
  updateInvitation,
  resendInvitation,
  cancelInvitation,
  acceptInvitation,
  declineInvitation,
  // HubSpot import functions
  getHubspotContactsCount,
  listHubspotContacts,
  startHubspotBulkImport,
  getImportJobStatus
};

export default invitationService;
